import React, { useEffect, useMemo, useRef, useState } from "react";
import "./mobile-captcha-challenge.css";
import {
  appendMobileCaptchaQueryParam,
  getMobileCaptchaActionName,
  getMobileCaptchaRedirectUri,
} from "./mobile-captcha-challenge.functions";

const TURNSTILE_SITE_KEY =
  process.env.REACT_APP_TURNSTILE_SITE_KEY_MOBILE || process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

export default function MobileCaptchaChallenge() {
  const [errorMessage, setErrorMessage] = useState("");
  const [statusMessage, setStatusMessage] = useState("Complete verification to continue.");
  const [widgetReady, setWidgetReady] = useState(false);
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  const redirectUri = useMemo(() => getMobileCaptchaRedirectUri(), []);
  const actionName = useMemo(() => getMobileCaptchaActionName(), []);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) {
      setErrorMessage("Captcha is not configured for this environment.");
      return;
    }

    const onToken = (token) => {
      if (!token) {
        setErrorMessage("CAPTCHA verification failed. Please try again.");
        return;
      }

      setStatusMessage("Verification complete. Returning to app...");

      let nextUrl = appendMobileCaptchaQueryParam(redirectUri, "token", token);
      nextUrl = appendMobileCaptchaQueryParam(nextUrl, "action", actionName);
      nextUrl = appendMobileCaptchaQueryParam(nextUrl, "provider", "turnstile");
      window.location.assign(nextUrl);
    };

    const onExpire = () => {
      setErrorMessage("Verification expired. Please complete CAPTCHA again.");
      setStatusMessage("Complete verification to continue.");
    };

    const onError = () => {
      setErrorMessage("CAPTCHA verification failed. Please try again.");
      setStatusMessage("Complete verification to continue.");
    };

    const renderWidget = () => {
      if (!window.turnstile || !containerRef.current || widgetIdRef.current !== null) {
        return;
      }

      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: onToken,
        "expired-callback": onExpire,
        "error-callback": onError,
      });

      setWidgetReady(true);
      setErrorMessage("");
    };

    if (window.turnstile) {
      renderWidget();
      return () => {};
    }

    let script = document.querySelector("script[data-turnstile-mobile]");
    if (!script) {
      script = document.createElement("script");
      script.setAttribute("data-turnstile-mobile", "true");
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.onload = renderWidget;
      document.body.appendChild(script);
    } else {
      script.addEventListener("load", renderWidget);
    }

    return () => {
      if (script) {
        script.onload = null;
        script.removeEventListener("load", renderWidget);
      }
    };
  }, [actionName, redirectUri]);

  return (
    <div className="mobileCaptchaChallenge">
      <div className="mobileCaptchaChallenge__card">
        {/* Header */}
        <h1 className="mobileCaptchaChallenge__title">Security Check</h1>
        <p className="mobileCaptchaChallenge__status">{statusMessage}</p>

        {/* Feedback */}
        {errorMessage ? (
          <div className="mobileCaptchaChallenge__error">{errorMessage}</div>
        ) : null}

        {/* CAPTCHA widget */}
        <div ref={containerRef} />

        {!widgetReady && !errorMessage ? (
          <p className="mobileCaptchaChallenge__loading">Loading CAPTCHA...</p>
        ) : null}
      </div>
    </div>
  );
}
